import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Menu } from 'antd';
import useMapActions from '../MapActions/useMapActions';
import { menuItemsFor } from './menuItems';
import './contextMenu.css';

// Keeps the menu this far (px) from the window edges.
const EDGE_MARGIN = 8;

const clampToWindow = (x, y, element) => {
    const { width, height } = element.getBoundingClientRect();
    return {
        x: Math.max(EDGE_MARGIN, Math.min(x, window.innerWidth - width - EDGE_MARGIN)),
        y: Math.max(EDGE_MARGIN, Math.min(y, window.innerHeight - height - EDGE_MARGIN)),
    };
};

/**
 * The right-click menu (D-037). `menu`: `{ kind: 'node' | 'edge' | 'pane', x, y, node?, edge? }` (client coordinates)
 * or null. `toFlow` turns a client point into a map position; `onEdit(node)` opens the Edit drawer.
 */
const MapContextMenu = ({ menu, onClose, onEdit, toFlow }) => {
    const actions = useMapActions();
    const containerRef = useRef(null);
    const [position, setPosition] = useState(null);

    useLayoutEffect(() => {
        setPosition(menu && containerRef.current ? clampToWindow(menu.x, menu.y, containerRef.current) : null);
    }, [menu]);

    useEffect(() => {
        if (!menu) return undefined;
        const closeOutside = (event) => {
            if (!containerRef.current?.contains(event.target)) onClose();
        };
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('pointerdown', closeOutside, true);
        document.addEventListener('keydown', closeOnEscape);
        window.addEventListener('wheel', onClose, { passive: true });
        window.addEventListener('resize', onClose);
        window.addEventListener('blur', onClose);
        return () => {
            document.removeEventListener('pointerdown', closeOutside, true);
            document.removeEventListener('keydown', closeOnEscape);
            window.removeEventListener('wheel', onClose);
            window.removeEventListener('resize', onClose);
            window.removeEventListener('blur', onClose);
        };
    }, [menu, onClose]);

    if (!menu) return null;

    const run = ({ key }) => {
        const { node, edge } = menu;
        const nodeIds = node ? actions.targetIds(node) : [];
        const point = toFlow({ x: menu.x, y: menu.y });
        if (key === 'copy') actions.copy(nodeIds);
        else if (key === 'paste' || key === 'paste-here') actions.paste(point);
        else if (key === 'duplicate') actions.duplicate(nodeIds);
        else if (key.startsWith('insert:')) actions.insertNode(node, key.slice('insert:'.length));
        else if (key === 'insert-here') actions.insertNodeAt(point);
        else if (key === 'edit') onEdit(node);
        else if (key === 'delete') actions.remove(nodeIds, edge ? [edge.id] : []);
        onClose();
    };

    return (
        <div
            ref={containerRef}
            className="map-context-menu"
            role="presentation"
            style={{ left: (position ?? menu).x, top: (position ?? menu).y, visibility: position ? 'visible' : 'hidden' }}
            onContextMenu={(event) => event.preventDefault()}
        >
            <Menu
                mode="vertical"
                selectable={false}
                items={menuItemsFor(menu.kind, { canPaste: actions.canPaste })}
                onClick={run}
                getPopupContainer={() => containerRef.current}
            />
        </div>
    );
};

export default MapContextMenu;
